import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Plus, LayoutGrid, Sparkles, ChevronLeft, ChevronDown } from 'lucide-react';
import { Button, Container, Empty, Stack, cn } from '@easymoving/ui';
import { DESTINATIONS, moveScenario, type Destination } from '@easymoving/types';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useBoxes, useItems, useMoveProfile } from '../hooks/useUserData';
import { computeItemStats } from '../lib/query/itemStats';
import { computeMeasureTotals } from '../lib/query/measure';
import { useUnits } from '../lib/units/useUnits';
import { dashboardLayout } from '../lib/query/dashboardLayout';
import { computeReadiness } from '../lib/query/readiness';
import { filterNeedsDecision } from '../lib/query/needs';
import { isMissingPhoto } from '../lib/query/itemFilters';
import { generateSmartPrompts } from '../lib/query/smartPrompts';
import { destinationTone, toneDot } from '../lib/taxonomy/tone';
import { AddItemTile } from '../components/items/AddItemTile';
import { StatCard } from '../components/app/StatCard';
import { ReadinessCard } from '../components/app/ReadinessCard';
import { SmartPrompts } from '../components/app/SmartPrompts';
import { ItemCard } from '../components/items/ItemCard';

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { items, loading } = useItems();
  const { boxes } = useBoxes();
  const { profile } = useMoveProfile();
  const { canEdit } = useWorkspace();
  const units = useUnits();
  const who = user?.displayName?.split(' ')[0];

  const scenario = moveScenario(profile?.originScope, profile?.destinationScope);
  const layout = dashboardLayout(scenario);

  const [destOpen, setDestOpen] = useState(() => {
    try {
      const stored = localStorage.getItem('easymoving.dashDestOpen');
      if (stored != null) return stored === '1';
    } catch {
      /* ignore */
    }
    // No saved preference: emphasizeDeclutter foregrounds the grid (expanded);
    // everything else keeps today's collapsed default.
    return layout.emphasizeDeclutter;
  });
  function toggleDest() {
    setDestOpen((v) => {
      const next = !v;
      try {
        localStorage.setItem('easymoving.dashDestOpen', next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }

  if (loading) {
    return (
      <Container className="pt-6">
        <p className="text-graphite py-12">{t('common.loading')}</p>
      </Container>
    );
  }

  const stats = computeItemStats(items);
  const { score } = computeReadiness(stats, boxes.length, profile);
  const prompts = generateSmartPrompts({
    stats,
    boxCount: boxes.length,
    profile,
    needsDecisionCount: filterNeedsDecision(items).length,
    missingPhotoCount: items.filter(isMissingPhoto).length,
  });
  const recent = items.slice(0, 4);
  const totals = computeMeasureTotals(items);

  // When decluttering is the main job, surface the give-away/sell/donate/discard
  // destinations first. Otherwise keep today's declared destination order.
  const DECLUTTER: Destination[] = ['give_away', 'sell', 'donate', 'discard'];
  const destinationOrder = layout.emphasizeDeclutter
    ? [...DESTINATIONS].sort((a, b) => Number(DECLUTTER.includes(b)) - Number(DECLUTTER.includes(a)))
    : DESTINATIONS;

  return (
    <Container className="pt-6">
      <Stack gap="lg">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl tracking-snug">
              {t('dashboard.hello')}
              {who ? ` ${who}` : ''}
            </h1>
            {scenario !== 'home_to_home' && (
              <p className="text-graphite mt-1">{t(`scenario.intro.${scenario}`)}</p>
            )}
            {items.length > 0 && (
              <p className="text-graphite mt-1">{t('dashboard.totalItems', { count: stats.total })}</p>
            )}
          </div>
          {canEdit && (
            <Button asChild className="shrink-0">
              <Link to="/app/items/new">
                <Plus className="size-5" />
                {t('gallery.addItem')}
              </Link>
            </Button>
          )}
        </div>

        {(() => {
          const hasAside = prompts.length > 0 || items.length > 0;
          return (
            <div className={cn('grid gap-4', hasAside && 'lg:grid-cols-3')}>
              <ReadinessCard
                score={score}
                moveDate={profile?.moveDate}
                hasProfile={!!profile}
                className={cn('h-full', hasAside && 'lg:col-span-2')}
              />
              {hasAside && (
                <aside className="flex flex-col gap-3 lg:max-w-md">
                  <SmartPrompts prompts={prompts} />
                  {items.length > 0 && layout.settleInProminence !== 'hero' && (
                    <Link
                      to="/app/settle-in"
                      className="surface px-4 py-3 flex items-center gap-3 hover:bg-surface-tint transition-colors"
                    >
                      <span className="size-9 shrink-0 rounded-full bg-petrol-tint text-petrol flex items-center justify-center">
                        <Sparkles className="size-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-[15px] block leading-tight">{t('settleIn.title')}</span>
                        <span className="text-graphite-soft text-[13px] block line-clamp-1">{t('settleIn.dashHint')}</span>
                      </span>
                      <ChevronLeft className="size-4 text-graphite-soft shrink-0 ltr:rotate-180" />
                    </Link>
                  )}
                </aside>
              )}
            </div>
          );
        })()}

        {items.length > 0 && layout.settleInProminence === 'hero' && (
          <Link
            to="/app/settle-in"
            className="surface-tint border border-petrol/20 px-5 py-4 flex items-center gap-4 hover:bg-petrol-tint/40 transition-colors"
            data-testid="settle-in-hero"
          >
            <span className="size-12 shrink-0 rounded-full bg-petrol text-white flex items-center justify-center">
              <Sparkles className="size-6" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="font-display text-lg tracking-snug block leading-tight">{t('settleIn.title')}</span>
              <span className="text-graphite text-[14px] block">{t('settleIn.dashHint')}</span>
            </span>
            <ChevronLeft className="size-5 text-petrol shrink-0 ltr:rotate-180" />
          </Link>
        )}

        {items.length === 0 ? (
          <Empty
            title={t('dashboard.noItems')}
            body={t('dashboard.startBody')}
            action={
              canEdit ? (
                <Button asChild size="lg">
                  <Link to="/app/items/new">
                    <Plus className="size-5" />
                    {t('gallery.addItem')}
                  </Link>
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div>
              <button
                type="button"
                onClick={toggleDest}
                aria-expanded={destOpen}
                className="w-full flex items-center gap-2 mb-2"
              >
                <h2 className="smallcaps text-graphite">{t('dashboard.byDestination')}</h2>
                <ChevronDown
                  className={cn('size-4 text-graphite-soft transition-transform', destOpen && 'rotate-180')}
                />
              </button>

              {destOpen ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2.5" data-testid="destination-grid">
                  <StatCard label={t('dashboard.allItems')} count={stats.total} tone="ink" to="/app/items" />
                  {destinationOrder.map((d) => (
                    <StatCard
                      key={d}
                      label={t(`enum.destination.${d}`)}
                      count={stats.byDestination[d]}
                      tone={destinationTone(d)}
                      to={`/app/items?destination=${d}`}
                    />
                  ))}
                  <StatCard label={t('nav.boxes')} count={boxes.length} tone="neutral" to="/app/boxes" />
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="destination-summary">
                  <SummaryChip to="/app/items" label={t('dashboard.allItems')} count={stats.total} />
                  {destinationOrder.filter((d) => stats.byDestination[d] > 0).map((d) => (
                    <SummaryChip
                      key={d}
                      to={`/app/items?destination=${d}`}
                      label={t(`enum.destination.${d}`)}
                      count={stats.byDestination[d]}
                      dot={toneDot[destinationTone(d)]}
                    />
                  ))}
                </div>
              )}
            </div>

            {layout.inventoryDensity !== 'slim' && (totals.weightItems > 0 || totals.volumeItems > 0) && (
              <div>
                <h2 className="smallcaps text-graphite mb-2">{t('dashboard.totals')}</h2>
                <div className="grid grid-cols-2 gap-2.5" data-testid="totals">
                  {totals.weightItems > 0 && (
                    <div className="surface p-4">
                      <div className="text-graphite-soft text-[12px]">{t('dashboard.totalWeight')}</div>
                      <div className="font-display text-2xl tracking-snug tnum mt-0.5">
                        {units.formatWeight(totals.weightKg)}
                      </div>
                      <div className="text-graphite-soft text-[11px] mt-1">
                        {t('dashboard.fromItems', { count: totals.weightItems })}
                      </div>
                    </div>
                  )}
                  {totals.volumeItems > 0 && (
                    <div className="surface p-4">
                      <div className="text-graphite-soft text-[12px]">{t('dashboard.totalVolume')}</div>
                      <div className="font-display text-2xl tracking-snug tnum mt-0.5">
                        {units.formatVolume(totals.volumeM3)}
                      </div>
                      <div className="text-graphite-soft text-[11px] mt-1">
                        {t('dashboard.fromItems', { count: totals.volumeItems })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="smallcaps text-graphite">{t('dashboard.recent')}</h2>
                <Link
                  to="/app/items"
                  className="text-petrol text-[13px] hover:underline inline-flex items-center gap-1"
                >
                  <LayoutGrid className="size-3.5" />
                  {t('dashboard.viewAll')}
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {recent.map((item) => (
                  <ItemCard key={item.id} item={item} />
                ))}
                <AddItemTile />
              </div>
            </div>
          </>
        )}
      </Stack>
    </Container>
  );
}

function SummaryChip({
  to,
  label,
  count,
  dot,
}: {
  to: string;
  label: string;
  count: number;
  dot?: string;
}) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 rounded-full bg-surface border border-rule ps-2.5 pe-3 py-1 text-[13px] hover:bg-surface-tint transition-colors"
    >
      {dot ? <span className={cn('size-2 rounded-full', dot)} /> : null}
      <span className="font-semibold tnum">{count}</span>
      <span className="text-graphite">{label}</span>
    </Link>
  );
}
